// Shared by the Cloudflare catch-all function and the client-side router, so a
// route cannot present one canonical to a crawler and another after Vue mounts.
// Keep this module free of DOM and Node types: the Workers build compiles it
// without the DOM lib.

import {DEFAULT_LOCALE, LOCALES, type Locale} from '../i18n/locales';
import {localizePath, parseLocalePath, type LocalePath, type LocalizedPath} from '../i18n/locale-path';
import {MESSAGES, type Messages} from '../i18n/messages';

export const SITE_URL = 'https://lopaka.app';

export const DEFAULT_TITLE = MESSAGES[DEFAULT_LOCALE].meta.home.title;
export const DEFAULT_DESCRIPTION = MESSAGES[DEFAULT_LOCALE].meta.home.description;
export const DEFAULT_IMAGE = `${SITE_URL}/assets/img/lopaka-preview.jpg`;

/** One `<link rel="alternate" hreflang>` of a page with language versions. */
export interface PageAlternate {
    hreflang: string;
    href: string;
}

export interface PageMetadata {
    title: string;
    description: string;
    canonical: string;
    indexable: boolean;
    /** The page language; set only for pages with a version per language. */
    locale?: Locale;
    /** Every language version of the page plus `x-default`; empty for other pages. */
    alternates: PageAlternate[];
}

interface RouteMetadata {
    title: string;
    description: string;
    indexable?: false;
    /** App pages without language versions take their title from the app language. */
    appTitle?: keyof Messages['meta']['appPages'];
}

// The pages with a version per language (`/`, `/price`, `/gallery` and their
// `/es/`, `/de/` addresses) take their title and description from the
// dictionary of the page language.
const LOCALIZED_PAGE_META: Record<LocalizedPath, Exclude<keyof Messages['meta'], 'app'>> = {
    '/': 'home',
    '/gallery': 'gallery',
    '/price': 'price',
};

function appPage(appTitle: keyof Messages['meta']['appPages']): RouteMetadata {
    return {
        title: MESSAGES[DEFAULT_LOCALE].meta.appPages[appTitle],
        description: DEFAULT_DESCRIPTION,
        indexable: false,
        appTitle,
    };
}

// Public pages get their own title/description so they stop competing for the
// same snippet; everything behind auth or mid-checkout stays out of the index.
const ROUTE_METADATA: Record<string, RouteMetadata> = {
    '/careers': {
        title: 'Careers at Lopaka',
        description: 'Open roles at Lopaka, the visual UI editor and code generator for embedded displays.',
    },
    // Signed-out visitors are redirected to the homepage from these.
    '/assets': appPage('assets'),
    '/login': appPage('login'),
    '/login-expired': appPage('loginExpired'),
    '/projects': appPage('projects'),
    '/upgrade': appPage('upgrade'),
    '/success': appPage('success'),
};

// Detail pages render per-project metadata that is injected server-side or set
// by the view itself once its data arrives, so the router must not overwrite it.
const SELF_MANAGED_PREFIXES = ['/gallery/', '/editor/', '/canvas/', '/careers/', '/redirect/'];

// Routes the SPA router answers without their own metadata entry: checkout
// entry points, the sandbox alias, and the explicit 404 view. They are listed
// so the server can tell "a real page the app renders" from "a URL nothing
// serves", which is the difference between a 200 and a 404.
const UNLISTED_APP_ROUTES = [
    '/buy_plus',
    '/buy_plus_annual',
    '/buy_plus_lifetime',
    '/buy_plus_week',
    '/buy_hobby',
    '/buy_hobby_annual',
    '/sandbox',
    '/404',
];

export function normalizePath(path: string): string {
    const [pathname] = path.split(/[?#]/);
    return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

export function canonicalUrlFor(path: string): string {
    const [pathname] = path.split(/[?#]/);
    const trimmed = pathname.replace(/\/+$/, '');
    return `${SITE_URL}${trimmed}`;
}

/**
 * The absolute address of a localized page in a language. The root keeps its
 * trailing slash (`https://lopaka.app/es/`), the same form the router and the
 * sitemap use, so canonical, hreflang, and sitemap URLs match exactly.
 */
export function localizedUrlFor(basePath: LocalizedPath, locale: Locale): string {
    return `${SITE_URL}${localizePath(basePath, locale)}`;
}

/** Every language version of a localized page, plus `x-default` pointing at English. */
export function languageAlternates(basePath: LocalizedPath): PageAlternate[] {
    return [
        ...LOCALES.map((locale) => ({hreflang: locale.hreflang, href: localizedUrlFor(basePath, locale.code)})),
        {hreflang: 'x-default', href: localizedUrlFor(basePath, DEFAULT_LOCALE)},
    ];
}

function localizedPageMetadata({locale, basePath}: LocalePath): PageMetadata {
    const {title, description} = MESSAGES[locale].meta[LOCALIZED_PAGE_META[basePath]];
    return {
        title,
        description,
        canonical: localizedUrlFor(basePath, locale),
        indexable: true,
        locale,
        alternates: languageAlternates(basePath),
    };
}

/**
 * Returns the metadata for a route, or null when the route manages its own head
 * tags. Unknown paths resolve to a non-indexable page so the SPA 404 fallback
 * never presents itself as a canonical, indexable URL. `appLocale` titles the
 * app pages (`/projects`, …) in the user's language; the server omits it and
 * serves English.
 */
export function resolvePageMetadata(path: string, appLocale: Locale = DEFAULT_LOCALE): PageMetadata | null {
    const normalized = normalizePath(path);

    if (SELF_MANAGED_PREFIXES.some((prefix) => normalized.startsWith(prefix))) return null;

    const localized = parseLocalePath(normalized);
    if (localized) return localizedPageMetadata(localized);

    const route = ROUTE_METADATA[normalized];
    if (route) {
        return {
            title: route.appTitle ? MESSAGES[appLocale].meta.appPages[route.appTitle] : route.title,
            description: route.description,
            canonical: canonicalUrlFor(normalized),
            indexable: route.indexable !== false,
            alternates: [],
        };
    }

    return {
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        canonical: canonicalUrlFor(normalized),
        indexable: false,
        alternates: [],
    };
}

/**
 * True when the SPA router has a view for this path. A path that no route
 * matches renders the NotFound view, so the server answers it with a 404
 * instead of a 200 carrying the app shell.
 */
export function isKnownAppRoute(path: string): boolean {
    const normalized = normalizePath(path);

    if (SELF_MANAGED_PREFIXES.some((prefix) => normalized.startsWith(prefix))) return true;
    if (parseLocalePath(normalized)) return true;
    if (normalized in ROUTE_METADATA) return true;
    return UNLISTED_APP_ROUTES.includes(normalized);
}
